/**
 * The written debrief's answers, as taps: how it went, before and after,
 * and the two optional lines; and the two fields the report has always
 * filed, composed from them (and the steps they ticked). Logic only, so the
 * screen can stay a component.
 */
export type Outcome = "well" | "sideways" | "half";

export const OUTCOMES: {
  id: Outcome;
  label: string;
  sub: string;
  text: string;
}[] = [
  {
    id: "well",
    label: "Did it. Went well.",
    sub: "The whole thing, the way you hoped.",
    text: "Did it, and it went well.",
  },
  {
    id: "sideways",
    label: "Did it. Went sideways.",
    sub: "Still counts. Those are the good stories.",
    text: "Did it, and it went sideways.",
  },
  {
    id: "half",
    label: "Half of it.",
    sub: "Say what happened. Half is in the file too.",
    text: "Got halfway.",
  },
];

export const FEELINGS = ["Nervous", "Flat", "Fine", "Good", "Alive"] as const;
export type Feeling = (typeof FEELINGS)[number];

export interface DebriefAnswers {
  outcome: Outcome | null;
  detail: string;
  before: Feeling | null;
  after: Feeling | null;
  line: string;
}

export const EMPTY_ANSWERS: DebriefAnswers = {
  outcome: null,
  detail: "",
  before: null,
  after: null,
  line: "",
};

/** The account, from the steps: "Spotted someone. Walked over. Asked." */
export const accountFrom = (steps: string[]) => {
  const lines = steps
    .map((s) => s.trim().replace(/[.!?]+$/, ""))
    .filter(Boolean);
  return lines.length ? lines.join(". ").concat(".") : "";
};

/** The two fields the report files, from the taps. */
export function composeReport(a: DebriefAnswers, steps: string[]) {
  const outcome = OUTCOMES.find((o) => o.id === a.outcome)?.text ?? "";
  const whatIDid = [outcome, accountFrom(steps), a.detail.trim()]
    .filter(Boolean)
    .join(" ");
  const howItFelt = [
    a.before && a.after ? `Before: ${a.before}. After: ${a.after}.` : "",
    a.line.trim(),
  ]
    .filter(Boolean)
    .join(" ");
  return { whatIDid, howItFelt };
}

/** Every tap made; with no steps to write the account from, their own line too. */
/** Three taps and a line in their own words. */
export const answersReady = (a: DebriefAnswers) =>
  !!a.outcome && !!a.before && !!a.after && !!a.detail.trim();

/** One sentence of recognition for the answer: the cheapest loyalty there is. */
export function reactionFor(a: DebriefAnswers): string {
  const rank = (f: string) => FEELINGS.indexOf(f as (typeof FEELINGS)[number]);
  const up = a.before && a.after ? rank(a.after) - rank(a.before) : 0;
  if (a.outcome === "half")
    return "Half is in the file. Most people never open the door. Tomorrow we finish it.";
  if (a.outcome === "sideways")
    return up > 0
      ? "Went sideways and you still came out better. Those are the ones you'll tell later."
      : "Sideways, and you stayed in it. That's the job. Those are the ones you'll tell later.";
  if (a.after === "Alive")
    return "Alive. That's the whole point of the agency.";
  if (up >= 2) return `${a.before} to ${a.after}. That jump is the mission.`;
  if (up > 0) return "Better after than before. That's how it compounds.";
  return "Done is done. The lock doesn't care how it felt.";
}
